import {
    DocumentIcon,
    DownloadIcon,
    EuroCoinIcon,
    GiftIcon,
    ShoppingCartIcon,
    TruckIcon,
    UserIcon,
} from '@synergy/theme-next';
import {
    FileTypeIcon,
    PoolAccordion,
    PoolAccordionDetails,
    PoolAccordionItem,
    PoolAccordionSummary,
    PoolAvatar,
    PoolLink,
    PoolRow,
    PoolTypography,
} from '@synergy/components-next';
import { FormattedMessage } from 'react-intl';
import { List, Stack, styled, useMediaQuery, useTheme } from '@mui/material';
import { ReactElement } from 'react';
import { downloadUrl } from '@synergy/utils';

import { BonusConditionsPresenter } from '../../../components/bonus-conditions-presenter/bonus-condition-presenter';
import { PaymentTermText } from '../../../components/payment-term-text/payment-term-text';
import { PoolFile } from '../../../models/pool-file';
import { supplierDetailSelector } from '../../../store/supplier-selectors';
import { useAppSelector } from '../../../../store/store';

const DocumentRow = styled(Stack)(({ theme }) => ({
    height: 56,
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    cursor: 'pointer',
    '&:not(:last-child)': {
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
    },
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    },
}));
export const SupplerInfoAccordion = (): ReactElement | null => {
    const isMobile = useMediaQuery(useTheme().breakpoints.down('sm'));
    const supplier = useAppSelector(supplierDetailSelector);

    const handleDocumentDownload = (document: PoolFile): void => {
        downloadUrl(document.path, document.title);
    };

    if (!supplier) {
        return null;
    }

    return (
        <Stack sx={{ width: '100%', pt: { sm: 14 } }} gap={isMobile ? 2 : 4}>
            <PoolTypography variant="h5" translationId="app.supplierDirectory.infos" />

            <Stack direction={isMobile ? 'column' : 'row'} gap={isMobile ? 0 : 6}>
                <PoolAccordion flex={1}>
                    <PoolAccordionItem disabled={!supplier.termsOfDelivery}>
                        <PoolAccordionSummary>
                            <TruckIcon />
                            <FormattedMessage id="app.supplier.termsOfDelivery" />
                        </PoolAccordionSummary>
                        <PoolAccordionDetails>{supplier.termsOfDelivery}</PoolAccordionDetails>
                    </PoolAccordionItem>

                    <PoolAccordionItem disabled={!supplier.conditionsOfPurchase}>
                        <PoolAccordionSummary>
                            <ShoppingCartIcon />
                            <FormattedMessage id="app.supplier.conditionsOfPurchase" />
                        </PoolAccordionSummary>
                        <PoolAccordionDetails>{supplier.conditionsOfPurchase}</PoolAccordionDetails>
                    </PoolAccordionItem>

                    <PoolAccordionItem disabled={supplier.documents.length === 0}>
                        <PoolAccordionSummary>
                            <DocumentIcon />
                            <FormattedMessage id="app.common.documents" />
                        </PoolAccordionSummary>

                        <PoolAccordionDetails sx={{ mr: 0 }}>
                            <List sx={{ p: 0 }}>
                                {supplier.documents.map(document => (
                                    <DocumentRow key={document.id} onClick={() => handleDocumentDownload(document)}>
                                        <PoolRow gap={1.25}>
                                            <FileTypeIcon type={document.type} />
                                            <PoolTypography variant="body1">{document.title}</PoolTypography>
                                        </PoolRow>
                                        <DownloadIcon />
                                    </DocumentRow>
                                ))}
                            </List>
                        </PoolAccordionDetails>
                    </PoolAccordionItem>
                </PoolAccordion>

                <PoolAccordion flex={1}>
                    <PoolAccordionItem disabled={!supplier.paymentTerm && !supplier.isChargingSupplier}>
                        <PoolAccordionSummary>
                            <EuroCoinIcon />
                            <FormattedMessage id="app.supplier.termsOfPayment" />
                        </PoolAccordionSummary>
                        <PoolAccordionDetails>
                            <PaymentTermText
                                isChargingSupplier={supplier.isChargingSupplier}
                                paymentTerm={supplier.paymentTerm}
                            />
                        </PoolAccordionDetails>
                    </PoolAccordionItem>
                    <PoolAccordionItem disabled={supplier.conditions.length === 0}>
                        <PoolAccordionSummary>
                            <GiftIcon />
                            <FormattedMessage id="app.supplier.bonusYear" />
                        </PoolAccordionSummary>
                        <PoolAccordionDetails>
                            <BonusConditionsPresenter supplierConditions={supplier.conditions} />
                        </PoolAccordionDetails>
                    </PoolAccordionItem>
                    <PoolAccordionItem disabled={!supplier.mainContact}>
                        <PoolAccordionSummary>
                            <UserIcon />
                            <FormattedMessage id="app.supplier.mainContact" />
                        </PoolAccordionSummary>
                        <PoolAccordionDetails>
                            {supplier.mainContact && (
                                <PoolRow sx={{ mt: { sm: 0.5 }, ml: { sm: 2.5 } }} gap={isMobile ? 1 : 2}>
                                    <PoolAvatar
                                        sx={{ width: 72, height: 72, fontSize: '1.375rem' }}
                                        firstName={supplier.mainContact?.firstName}
                                        lastName={supplier.mainContact?.lastName}
                                        styleType="contain"
                                    />
                                    <Stack typography="body2">
                                        {supplier.mainContact.fullName && (
                                            <strong>{supplier.mainContact.fullName}</strong>
                                        )}
                                        {supplier.mainContact.phone && (
                                            <PoolLink href={`tel:${supplier.mainContact.phone}`}>
                                                {supplier.mainContact.phone}
                                            </PoolLink>
                                        )}
                                        {supplier.mainContact.email && (
                                            <PoolLink
                                                href={`mailto:${supplier.mainContact.email}`}
                                                sx={{ overflow: 'hidden' }}
                                            >
                                                {supplier.mainContact.email}
                                            </PoolLink>
                                        )}
                                    </Stack>
                                </PoolRow>
                            )}
                        </PoolAccordionDetails>
                    </PoolAccordionItem>
                </PoolAccordion>
            </Stack>
        </Stack>
    );
};
